import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Slider from 'react-slick'
import appIcon from '../../assets/img/logo-aleph-rosu.png'
import alephLogo from '../../assets/img/Aleph Logo.svg'
import lastFrameImg from '../../assets/img/last_frame.png'
import VideoSplash from './VideoSplash'
import { trackHomepageView } from '../../services/usageTracking'
import './Home.css'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

const API_BASE = 'https://backend.alephmedia.ro/api'

// Mapare tipuri widget -> config
const WIDGET_TYPE_MAP = {
  'live-streams': { nameField: 'name', linkBase: '/v4.0/live' },
  'sites': { nameField: 'name', linkBase: '/v4.0/sites' },
  'programs': { nameField: 'title', linkBase: '/v4.0/programs' },
  'people': { nameField: 'name', linkBase: '/v4.0/people' },
  'radios': { nameField: 'title', linkBase: '/v4.0/radios' },
  'watching': { nameField: 'title', linkBase: '/v4.0/programs' },
  'whats-new': { nameField: 'title', linkBase: '/v4.0/programs' },
  'news': { nameField: 'title', linkBase: null },
  'alephia': { nameField: 'title', linkBase: null }
}

function decodeHtmlEntities(text) {
  if (!text) return ''
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

function extractName(item = {}, field) {
  if (!item) return '—'
  let rawText = item[field] || item.name || item.title || '—'
  return decodeHtmlEntities(rawText)
}

function extractJWPlayerMediaId(url) {
  if (!url || typeof url !== 'string') return null
  let match = url.match(/manifests\/([^\/]+)\.m3u8/i)
  if (match) return match[1]
  match = url.match(/jwplayer\.com\/videos\/([^-]+)/i)
  if (match) return match[1]
  match = url.match(/jwplatform\.com\/videos\/([^-]+)/i)
  if (match) return match[1]
  return null
}

function getJWPlayerPosterUrl(mediaId) {
  if (!mediaId) return null
  return `https://cdn.jwplayer.com/v2/media/${mediaId}/poster.jpg`
}

function getJWPlayerPreviewUrl(mediaId) {
  if (!mediaId) return null
  return `https://cdn.jwplayer.com/v2/media/${mediaId}/poster.mp4`
}

function isValidImageUrl(url) {
  if (!url || typeof url !== 'string') return false
  const videoExtensions = ['.m3u8', '.mp4', '.webm', '.ogg', '.mov', '.avi']
  const lowerUrl = url.toLowerCase()
  return !videoExtensions.some(ext => lowerUrl.includes(ext)) && 
         (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://') || lowerUrl.startsWith('data:') || lowerUrl.startsWith('/'))
}

function extractImage(item = {}, type) {
  if (!item) return null
  let image = type === 'desktop' 
    ? (item.image_16_9 || item.image_9_16 || item.image)
    : (item.image_9_16 || item.image_16_9 || item.image)
  return isValidImageUrl(image) ? image : null
}

function getUserFirstName() {
  try {
    const stored = JSON.parse(localStorage.getItem('userData') || '{}')
    if (stored?.first_name) return stored.first_name
    if (stored?.name) return String(stored.name).split(' ')[0]
  } catch (e) {}
  return 'invitat'
}

function getUserAvatarUrl() {
  try {
    const stored = JSON.parse(localStorage.getItem('userData') || '{}')
    return stored?.avatar_url || null
  } catch (e) {}
  return null
}

function isUserPremium() {
  try {
    const stored = JSON.parse(localStorage.getItem('userData') || '{}')
    return stored?.subscription_type === 'premium' && stored?.subscription_status === 'active'
  } catch (e) {}
  return false
}

const ArrowButton = ({ direction, onClick }) => {
  const isNext = direction === 'next'
  return (
    <button
      className={`v4-arrow v4-arrow-${direction}`}
      type="button"
      onClick={onClick}
      aria-label={isNext ? 'Vezi următoarele' : 'Vezi precedentele'}
    >
      {isNext ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6l-6 6" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6l6 6" />
        </svg>
      )}
    </button>
  )
}

const Home = () => {
  const navigate = useNavigate()
  const [widgets, setWidgets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [userRefresh, setUserRefresh] = useState(0)
  const [showVideoSplash, setShowVideoSplash] = useState(() => !sessionStorage.getItem('v4_splash_shown'))
  const [homepageVisible, setHomepageVisible] = useState(() => !!sessionStorage.getItem('v4_splash_shown'))
  const [contentAnimated, setContentAnimated] = useState(() => !!sessionStorage.getItem('v4_splash_shown'))
  const [watchingEpisodes, setWatchingEpisodes] = useState([])
  const [alephiaStories, setAlephiaStories] = useState(null)
  
  const firstName = useMemo(() => getUserFirstName(), [userRefresh])
  const isLoggedIn = useMemo(() => !!localStorage.getItem('authToken'), [userRefresh])
  const avatarUrl = useMemo(() => getUserAvatarUrl(), [userRefresh])

  // Încarcă homepage-ul
  useEffect(() => {
    let isMounted = true
    async function loadHomepage() {
      setLoading(true)
      try {
        const res = await fetch(`${API_BASE}/v4/homepage`)
        if (!res.ok) throw new Error(`Failed to load homepage: ${res.status}`)
        
        const response = await res.json()
        
        if (isMounted && response?.success && response?.data) {
          setWidgets(response.data.widgets || [])
          setError(null)
          trackHomepageView()
        }
      } catch (err) {
        console.error('[V4Home] Failed to load homepage', err)
        if (isMounted) setError('Nu am putut încărca homepage-ul. Încearcă din nou.')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadHomepage()
    return () => { isMounted = false }
  }, [])

  useEffect(() => {
    if (!widgets.length) return
    const hasAlephiaWidget = widgets.some(w => w.type === 'alephia')
    if (!hasAlephiaWidget) return

    const token = localStorage.getItem('authToken')
    if (!token) return

    let cancelled = false
    async function loadAlephia() {
      try {
        const res = await fetch(`${API_BASE}/partner-feed/stories?per_page=20`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
        })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled && data.success && data.data?.length > 0) {
          setAlephiaStories(data.data.map(s => ({
            id: s.id,
            title: s.title,
            image: s.image_url || null,
            date: s.received_at || s.created_at,
            category: s.category,
            content: s.content,
          })))
        } else if (!cancelled) {
          setAlephiaStories([])
        }
      } catch (err) {
        console.error('[V4Home] Alephia stories load failed', err)
        if (!cancelled) setAlephiaStories([])
      }
    }
    loadAlephia()
    return () => { cancelled = true }
  }, [widgets])

  useEffect(() => {
    const handleFocus = () => setUserRefresh(prev => prev + 1)
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('userData')
    localStorage.removeItem('subscriptionId')
    localStorage.removeItem('subscriptionLevel')
    localStorage.removeItem('__paypal_storage__')
    navigate('/v4.0/login')
    window.location.reload()
  }

  // Funcție pentru episoadele watching
  function getWatchingEpisodes(allPrograms, maxElements, daysBack) {
    try {
      const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
      const now = Date.now()
      const daysBackMs = (daysBack || 30) * 24 * 60 * 60 * 1000
      const episodes = []
      
      Object.keys(watchData).forEach(programId => {
        const programEpisodes = watchData[programId]
        const program = allPrograms.find(p => p.id === parseInt(programId))
        if (!program) return
        
        Object.keys(programEpisodes).forEach(episodeId => {
          const ep = programEpisodes[episodeId]
          if (!ep.completed && ep.lastWatched && (now - ep.lastWatched) <= daysBackMs) {
            episodes.push({
              id: parseInt(episodeId),
              programId: parseInt(programId),
              title: ep.episodeTitle || 'Episod',
              image_16_9: ep.poster_image_16_9 || program.image_16_9,
              image_9_16: ep.poster_image_9_16 || program.image_9_16,
              program,
              currentTime: ep.currentTime || 0,
              lastWatched: ep.lastWatched
            })
          }
        })
      })
      
      episodes.sort((a, b) => b.lastWatched - a.lastWatched)
      return episodes.slice(0, maxElements || 10)
    } catch (error) {
      return []
    }
  }

  // Renderează un widget
  const renderWidget = (widget, index) => {
    const config = WIDGET_TYPE_MAP[widget.type]
    if (!config) return null

    let items = widget.items || []
    const settings = widget.settings || {}

    // Alephia: date încărcate client-side, doar pentru premium cu știri
    if (widget.type === 'alephia') {
      if (!alephiaStories || alephiaStories.length === 0) return null
      const limit = settings.limit || 10
      items = alephiaStories.slice(0, limit)
    }
    
    // Pentru watching, filtrează din localStorage
    if (widget.type === 'watching') {
      const episodes = getWatchingEpisodes(items, settings.maxElements || 10, settings.daysBack || 30)
      if (episodes.length > 0) {
        if (watchingEpisodes.length === 0) setWatchingEpisodes(episodes)
        items = watchingEpisodes.length > 0 ? watchingEpisodes : episodes
      } else {
        items = []
      }
    }
    
    // Pentru news, filtrează după hoursBack
    if (widget.type === 'news') {
      const hoursBack = settings.hoursBack || 24
      const cutoffDate = new Date()
      cutoffDate.setHours(cutoffDate.getHours() - hoursBack)
      
      items = items.filter(item => {
        if (!item?.date) return false
        try {
          return new Date(item.date) >= cutoffDate
        } catch { return false }
      })
    }

    const title = settings.title || widget.label || 'Secțiune'
    if (items.length === 0) return null

    return (
      <section key={`widget-${widget.type}-${index}`} className="v4-section">
        <header className="v4-section-header">
          <h2>{title}</h2>
        </header>
        <div className="v4-row">
          <Slider
            slidesToShow={6}
            slidesToScroll={6}
            infinite={false}
            arrows
            dots
            nextArrow={<ArrowButton direction="next" />}
            prevArrow={<ArrowButton direction="prev" />}
            responsive={[
              { breakpoint: 1280, settings: { slidesToShow: 5, slidesToScroll: 5 } },
              { breakpoint: 1024, settings: { slidesToShow: 4, slidesToScroll: 4 } },
              { breakpoint: 768, settings: { slidesToShow: 4, slidesToScroll: 4, arrows: false, dots: true } },
              { breakpoint: 640, settings: { slidesToShow: 4, slidesToScroll: 4, arrows: false, dots: true } }
            ]}
          >
            {items.map((item, itemIndex) => {
              const itemId = item.id
              
              // Alephia stories
              if (widget.type === 'alephia') {
                const alephiaImage = isValidImageUrl(item.image) ? item.image : appIcon
                const alephiaTitle = decodeHtmlEntities(item.title || 'Știre Alephia')
                return (
                  <Link to="/v4.0/partner-feed" className="v4-card v4-card--link" key={`alephia-${item.id}-${itemIndex}`}>
                    <div className="v4-card-image">
                      <img src={alephiaImage} alt={alephiaTitle} loading="lazy" onError={(e) => { if (e.target.src !== appIcon) e.target.src = appIcon }} />
                    </div>
                    {settings.showCardText && <div className="v4-card-title">{alephiaTitle}</div>}
                    {item.category && <div className="v4-card-badge">{item.category}</div>}
                  </Link>
                )
              }

              // Pentru news cu video
              let newsImage = null
              let newsPreviewVideo = null
              
              if (widget.type === 'news') {
                const manifestUrl = item.video_manifest
                if (manifestUrl) {
                  const mediaId = extractJWPlayerMediaId(manifestUrl)
                  if (mediaId) {
                    newsPreviewVideo = getJWPlayerPreviewUrl(mediaId)
                    newsImage = getJWPlayerPosterUrl(mediaId)
                  }
                }
                if (!newsImage) {
                  newsImage = isValidImageUrl(item.image) ? item.image : null
                }
              }
              
              const desktopImage = widget.type === 'news' 
                ? (newsImage || appIcon)
                : (extractImage(item, 'desktop') || appIcon)
              const mobileImage = widget.type === 'news'
                ? (newsImage || appIcon)
                : (extractImage(item, 'mobile') || desktopImage)
              const itemTitle = extractName(item, config.nameField)
              
              // Construim link-ul
              let linkTo = null
              let linkState = {}
              
              if (widget.type === 'watching' && item.programId) {
                linkTo = `/v4.0/programs/${item.programId}/episodes/${itemId}`
                linkState = { program: item.program, resumeFrom: item.currentTime || 0 }
              } else if (widget.type === 'news' && item.link) {
                linkTo = item.link
              } else if (itemId && config.linkBase) {
                linkTo = `${config.linkBase}/${itemId}`
              }
              
              const cardContent = (
                <>
                  <div className="v4-card-image">
                    {widget.type === 'news' && newsPreviewVideo ? (
                      <>
                        <video
                          src={newsPreviewVideo}
                          poster={desktopImage}
                          autoPlay muted loop playsInline
                          className="v4-card-video-preview"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            const fallback = e.target.parentElement.querySelector('.v4-card-video-fallback')
                            if (fallback) fallback.style.display = 'block'
                          }}
                        />
                        <img src={desktopImage} alt={itemTitle} className="v4-card-video-fallback" style={{ display: 'none' }} loading="lazy" />
                      </>
                    ) : (
                      <picture>
                        <source media="(max-width: 768px)" srcSet={mobileImage} />
                        <img src={desktopImage} alt={itemTitle} loading="lazy" onError={(e) => { if (e.target.src !== appIcon) e.target.src = appIcon }} />
                      </picture>
                    )}
                  </div>
                  {settings.showCardText && <div className="v4-card-title">{itemTitle}</div>}
                </>
              )

              // News - link extern
              if (widget.type === 'news' && linkTo) {
                return (
                  <a href={linkTo} target="_blank" rel="noopener noreferrer" className="v4-card v4-card--link" key={`${widget.type}-${itemId}-${itemIndex}`}>
                    {cardContent}
                  </a>
                )
              }

              // Link intern
              if (linkTo) {
                return (
                  <Link to={linkTo} state={linkState} className="v4-card v4-card--link" key={`${widget.type}-${itemId}-${itemIndex}`}>
                    {cardContent}
                  </Link>
                )
              }

              return (
                <div className="v4-card" key={`${widget.type}-${itemId}-${itemIndex}`}>
                  {cardContent}
                </div>
              )
            })}
          </Slider>
        </div>
      </section>
    )
  }

  const handleVideoComplete = () => {
    sessionStorage.setItem('v4_splash_shown', 'true')
    // Ascunde splash-ul și arată homepage-ul cu fundal still
    setShowVideoSplash(false)
    setHomepageVisible(true)
    // După 50ms, declanșează animația de conținut (fade alb + widget slide)
    setTimeout(() => {
      setContentAnimated(true)
    }, 50)
  }

  return (
    <>
      {showVideoSplash && <VideoSplash onComplete={handleVideoComplete} />}
      <div className={`v4-root ${contentAnimated ? 'content-animated' : ''}`} style={{ backgroundImage: `url(${lastFrameImg})` }}>
        <div className={`v4-overlay ${homepageVisible && !showVideoSplash ? 'fade-in' : ''}`}>
          <header className="v4-header">
            <div className="v4-header-left">
              Pentru tine, <span>{firstName}</span>
              {!isLoggedIn && (
                <button
                  type="button"
                  className="v4-login-cta"
                  onClick={() => navigate('/v4.0/login')}
                >
                  Intră
                </button>
              )}
            </div>
            <div className="v4-header-center"></div>
            <div className="v4-header-right">
              <img src={alephLogo} alt="Aleph ix" className="v4-logo-wordmark" />
              {isLoggedIn && (
                <>
                  {avatarUrl ? (
                    <button className="v4-profile-btn v4-profile-btn--avatar" onClick={() => navigate('/v4.0/account')} aria-label="Profilul meu" title="Profilul meu">
                      <img src={avatarUrl} alt="Profil" className="v4-header-avatar" />
                    </button>
                  ) : (
                    <button className="v4-profile-btn" onClick={() => navigate('/v4.0/account')} aria-label="Profilul meu" title="Profilul meu">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </button>
                  )}
                  <button className="v4-logout-btn" onClick={handleLogout} aria-label="Deconectare" title="Deconectare">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </header>

          {loading && <div className="v4-status">Se încarcă homepage-ul...</div>}
          {error && <div className="v4-status v4-status-error">{error}</div>}

          {!loading && !error && (
            <div className="v4-sections">
              {widgets.length > 0 ? (
                widgets.map((widget, index) => renderWidget(widget, index))
              ) : (
                <div className="v4-empty-state">
                  <p>Nu există widget-uri configurate încă.</p>
                  <p className="v4-empty-hint">Folosește Homepage Builder din panoul de administrare pentru a configura această pagină.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Home
